import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function getAuthHeader() {
  const token = localStorage.getItem("wa_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Create an axios instance with auto-auth + active workspace header
const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("wa_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const wid = localStorage.getItem("wa_active_workspace");
  if (wid) {
    config.headers["X-Workspace-Id"] = wid;
  }
  return config;
});

export { authAxios, API };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("wa_token");
    if (!token) {
      setUser(null);
      setPermissions(null);
      setLoading(false);
      return;
    }
    try {
      const wid = localStorage.getItem("wa_active_workspace");
      const headers = { Authorization: `Bearer ${token}`, ...(wid ? { "X-Workspace-Id": wid } : {}) };
      const [userRes, permsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers }),
        axios.get(`${API}/auth/permissions`, { headers }).catch(() => ({ data: null })),
      ]);
      setUser(userRes.data);
      setPermissions(permsRes.data);
    } catch {
      localStorage.removeItem("wa_token");
      setUser(null);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("wa_token", data.token);
      setUser(data);
      // Fetch permissions after login
      try {
        const permsRes = await axios.get(`${API}/auth/permissions`, { headers: { Authorization: `Bearer ${data.token}` } });
        setPermissions(permsRes.data);
      } catch { /* ignore */ }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      // If we're on a tenant subdomain, pass it so the new client gets associated to that sub-admin
      let subdomain = null;
      try {
        const { detectSubdomain } = await import("@/contexts/TenantContext");
        subdomain = detectSubdomain() || null;
      } catch { /* ignore */ }
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name, subdomain });
      localStorage.setItem("wa_token", data.token);
      setUser(data);
      try {
        const permsRes = await axios.get(`${API}/auth/permissions`, { headers: { Authorization: `Bearer ${data.token}` } });
        setPermissions(permsRes.data);
      } catch { /* ignore */ }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await authAxios.post(`${API}/auth/logout`);
    } catch { /* ignore */ }
    localStorage.removeItem("wa_token");
    setUser(null);
    setPermissions(null);
  };

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem("wa_token");
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(data);
    } catch { /* ignore */ }
  }, []);

  const value = useMemo(() => ({ user, loading, permissions, login, register, logout, refreshMe }), [user, loading, permissions, refreshMe]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
